/**
 * Contexto global para controle de reprodução de áudio
 * 
 * Garante que apenas um áudio seja reproduzido por vez em toda aplicação.
 * Quando um novo áudio inicia, todos os outros são pausados automaticamente.
 */

import { createContext, useContext, useState, useRef, useCallback, ReactNode } from 'react';

// Tipagem do contexto
interface AudioPlayerContextType {
  // ID do áudio atualmente tocando (null = nenhum)
  currentPlayingId: string | null;
  // Registra um elemento de áudio no contexto
  registerAudio: (id: string, audioElement: HTMLAudioElement) => void;
  // Remove registro quando componente desmonta
  unregisterAudio: (id: string) => void;
  // Define qual áudio deve tocar (pausa todos os outros)
  setPlaying: (id: string | null) => void;
}

// Cria o contexto com valor inicial null
const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

/**
 * Provider que gerencia o estado global de reprodução de áudio
 * Deve envolver os componentes que utilizam AudioMessageBubble
 */
export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  // ID do áudio que está tocando atualmente
  const [currentPlayingId, setCurrentPlayingId] = useState<string | null>(null);
  
  // Ref para armazenar referências de todos os elementos de áudio registrados
  // Usar Map permite acesso O(1) e evita re-renders desnecessários
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());

  /**
   * Registra um elemento de áudio no mapa global
   * Chamado quando um AudioMessageBubble é montado
   */
  const registerAudio = useCallback((id: string, audioElement: HTMLAudioElement) => {
    audioElementsRef.current.set(id, audioElement);
  }, []);

  /**
   * Remove registro de um áudio quando o componente desmonta
   * Previne memory leaks e referências a elementos removidos do DOM
   */
  const unregisterAudio = useCallback((id: string) => {
    audioElementsRef.current.delete(id);
    // Se era o áudio tocando, limpa o estado
    setCurrentPlayingId(prev => prev === id ? null : prev);
  }, []);

  /**
   * Define qual áudio está tocando - pausa todos os outros automaticamente
   * Este é o método principal que garante apenas um áudio por vez
   */
  const setPlaying = useCallback((id: string | null) => {
    // Pausa todos os áudios exceto o que vai começar a tocar
    audioElementsRef.current.forEach((audio, playerId) => {
      if (playerId !== id && !audio.paused) {
        audio.pause();
      }
    });
    setCurrentPlayingId(id);
  }, []);

  return (
    <AudioPlayerContext.Provider value={{
      currentPlayingId,
      registerAudio,
      unregisterAudio,
      setPlaying,
    }}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

/**
 * Hook para consumir o contexto de áudio
 * Deve ser usado dentro de um AudioPlayerProvider
 */
export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer deve ser usado dentro de AudioPlayerProvider');
  }
  return context;
}
